import { BarChart3, Bell, Building2, Key, KeyRound, ScrollText, Terminal, User, Users } from "lucide-react";
import type { HelpArticle } from "../types";

export const managementArticles: HelpArticle[] = [
  // ─────────────────────────────────────────── Company
  {
    id: "company",
    title: "Company settings",
    icon: Building2,
    group: "management",
    summary: "View and edit your company profile — name, contact email and timezone — and see your tier and status.",
    routeMatch: ["/settings"],
    minRole: "tenant_admin",
    keywords: ["company", "tenant", "settings", "profile", "timezone", "contact email", "subscription", "tier"],
    overview:
      "**Company settings** (the *Company* item, also the gear in the top bar) is your tenant's profile. Edit the basics — company name, contact email and timezone — and see read-only account facts like your Tenant ID, subscription tier and status.\n\n" +
      "Only a **Company admin** can open this page.",
    concepts: [
      { term: "Tenant ID", definition: "The immutable identifier for your company. You can't change it; you'll occasionally need it (e.g. when creating a group key)." },
      { term: "Subscription tier", definition: "Your plan — Basic, Standard or Premium. It's shown here but changed via billing / your Riot IO contact, not on this page." },
      { term: "Status", definition: "Active or Suspended. Shown here read-only." },
    ],
    tasks: [
      {
        id: "edit-company",
        title: "Update your company details",
        minRole: "tenant_admin",
        steps: [
          { text: "Open **Company** from the Management menu (or the gear icon in the top bar)." },
          { text: "Edit **Company Name**, **Contact Email** and/or **Timezone** (e.g. `Africa/Johannesburg`)." },
          { text: "Click **Save Changes** — a green 'Settings saved successfully' confirms it." },
        ],
      },
    ],
    faqs: [
      { q: "How do I change my subscription tier or limits?", a: "Tier and limits aren't editable here — they're set by Riot IO. See your current limits under **Usage**, and contact your Riot IO administrator to change the plan." },
      { q: "I clicked Save and nothing seemed to happen.", a: "On success you'll see a green confirmation. If you don't, re-check your entries (the email field must be a valid address) and try again." },
    ],
    related: ["usage", "users", "audit-log"],
  },

  // ─────────────────────────────────────────── Group Keys
  {
    id: "group-keys",
    title: "Group Keys",
    icon: Key,
    group: "management",
    summary: "Create and manage the provisioning keys devices use to self-onboard into a group.",
    routeMatch: ["/settings/group-keys"],
    minRole: "tenant_admin",
    keywords: ["group key", "provisioning", "onboard", "enrol", "revoke", "device onboarding", "secret"],
    overview:
      "A **group provisioning key** is a secret a device presents the first time it connects, to enrol itself into a specific device group. This page is where you create those keys, copy them for embedding in firmware, and revoke or delete them.\n\n" +
      "Only a **Company admin** can manage group keys.",
    concepts: [
      { term: "Group key", definition: "The secret token (e.g. `gk_live_…`). The table shows a truncated preview; use the copy button to grab the full value." },
      { term: "Provisioned count", definition: "How many devices have onboarded using this key." },
      { term: "Max devices", definition: "An optional cap on how many devices the key may onboard. 'Unlimited' means no cap." },
      { term: "Revoke vs delete", definition: "**Revoke** is a soft stop — it blocks future onboarding but keeps the key's history and doesn't touch devices that already used it. **Delete** removes the key row permanently." },
    ],
    tasks: [
      {
        id: "create-group-key",
        title: "Create a group key",
        minRole: "tenant_admin",
        intro: "You'll need the target group's ID first — copy it from the Device groups page.",
        steps: [
          { text: "Open **Group Keys** and click **Create Key**." },
          { text: "Paste the **Group ID (UUID)** of the group devices should join, and add an optional **Description**." },
          { text: "Click **Create**. The new key appears in the list." },
          { text: "Click the **copy** icon next to the key to copy the full secret for your firmware/config." },
        ],
        result: "Devices flashed with this key onboard into the linked group automatically on first connect.",
      },
      {
        id: "revoke-key",
        title: "Revoke or delete a key",
        minRole: "tenant_admin",
        steps: [
          { text: "**Revoke** (on an active key): click **Revoke** and confirm — future onboarding with it stops; already-provisioned devices are unaffected." },
          { text: "**Delete**: click **Delete** and confirm to remove the key permanently." },
          { text: "To act on several at once, tick their checkboxes and use the bulk **Revoke** / **Delete** buttons." },
        ],
      },
    ],
    faqs: [
      { q: "Where do I get the Group ID?", a: "Open **Device groups**, click the group, and copy its ID. There's no group picker on the key form — you paste the UUID." },
      { q: "Will revoking a key break devices already using it?", a: "No. Revoking only blocks *future* onboarding. Devices that already provisioned keep working." },
    ],
    related: ["device-groups", "api-credentials", "devices"],
  },

  // ─────────────────────────────────────────── API Credentials
  {
    id: "api-credentials",
    title: "API Credentials",
    icon: Terminal,
    group: "management",
    summary: "Issue machine-to-machine credentials so a server integration can call the API on your behalf.",
    routeMatch: ["/settings/api-credentials"],
    minRole: "tenant_admin",
    keywords: ["api", "credential", "client id", "client secret", "oauth", "integration", "scope", "rotate", "token endpoint", "server"],
    overview:
      "**API Credentials** are client-id/client-secret pairs that let a server-to-server integration authenticate to the RIoT-FOTA API as your company, scoped to a device group and a set of permissions.\n\n" +
      "The **client secret is shown only once**, at creation or rotation — save it immediately. Only a **Company admin** can manage credentials.",
    concepts: [
      { term: "Client ID & secret", definition: "The credential's public identifier and its private secret. Your integration exchanges them at the token endpoint for an access token." },
      { term: "Scopes", definition: "What the credential may do: **Read devices**, **Read configuration**, and **Write / deploy configuration**. Grant the least it needs." },
      { term: "One-time secret", definition: "The secret (and token endpoint) appear once, in the 'Save your credential now' dialog. There's no way to see it again — if lost, rotate to get a new one." },
      { term: "Rotate / Revoke / Delete", definition: "**Rotate** issues a fresh secret and immediately invalidates the old one. **Revoke** disables the credential but keeps its history. **Delete** removes it permanently." },
    ],
    tasks: [
      {
        id: "create-credential",
        title: "Create an API credential",
        minRole: "tenant_admin",
        steps: [
          { text: "Open **API Credentials** and click **Create credential**." },
          { text: "Give it a **Name** (e.g. 'Fleet config sync'), pick the **Group** it's scoped to, and tick the **Scopes** it needs." },
          { text: "Click **Create**. The **Save your credential now** dialog appears." },
          { text: "**Copy the Client ID, Client secret and Token endpoint immediately** and store them securely, then click **I've saved it**.", note: "The secret is never shown again." },
        ],
      },
      {
        id: "rotate-revoke",
        title: "Rotate or revoke a credential",
        minRole: "tenant_admin",
        steps: [
          { text: "**Rotate** (active credentials): click **Rotate**, confirm, and save the new secret from the dialog. The old secret stops working at once — update your integration." },
          { text: "**Revoke**: click **Revoke** to disable it while keeping its history; integrations using it will start receiving 403s." },
          { text: "**Delete**: click **Delete** to remove it permanently (its audit history is retained)." },
        ],
      },
    ],
    faqs: [
      { q: "I lost the client secret — can I retrieve it?", a: "No. The secret is shown only once. Use **Rotate** to issue a new secret, then update your integration with it (the old one stops working immediately)." },
      { q: "How is this different from a Group Key?", a: "A **Group Key** onboards *devices*; an **API Credential** lets a *server/back-office system* call the API. Different jobs." },
    ],
    related: ["group-keys", "device-groups", "audit-log"],
  },

  // ─────────────────────────────────────────── Usage
  {
    id: "usage",
    title: "Usage & limits",
    icon: BarChart3,
    group: "management",
    summary: "See your subscription tier and how much of each quota you're using.",
    routeMatch: ["/settings/usage"],
    minRole: "tenant_admin",
    keywords: ["usage", "limits", "quota", "tier", "plan", "devices", "users", "firmware", "concurrent tasks"],
    overview:
      "**Usage** shows your current plan and how much of each resource quota you've consumed against the plan limit — Devices, Users, Firmware Versions, Device Groups and Concurrent Tasks.\n\n" +
      "It's read-only. The bars turn amber above 70% and red above 90% so you can see when you're approaching a ceiling. Company admins only.",
    concepts: [
      { term: "Quota bar", definition: "Current usage over the limit for each resource. 'Unlimited' means no cap on that resource." },
      { term: "Tier", definition: "Your plan (Basic / Standard / Premium) and the default limits that come with it." },
    ],
    tasks: [
      {
        id: "check-usage",
        title: "Check whether you're near a limit",
        minRole: "tenant_admin",
        steps: [
          { text: "Open **Usage**." },
          { text: "Read the **Current Plan** card for your tier, and the **Resource Limits** bars for each quota." },
          { text: "If any bar is amber/red, plan ahead — creating past a limit will be refused." },
        ],
      },
    ],
    faqs: [
      { q: "Can I raise a limit here?", a: "No — limits are enforced by your plan. Contact your Riot IO administrator to change your tier or request an override." },
      { q: "A create action failed with a limit error.", a: "You've hit that resource's ceiling. Free some up (e.g. delete unused firmware) or ask for a higher limit." },
    ],
    related: ["company", "devices", "users"],
  },

  // ─────────────────────────────────────────── Users
  {
    id: "users",
    title: "Users",
    icon: Users,
    group: "management",
    summary: "Invite people to your company, set their roles, and enable, disable or remove them.",
    routeMatch: ["/users"],
    minRole: "tenant_admin",
    keywords: ["users", "invite", "role", "viewer", "operator", "admin", "disable", "deactivate", "delete", "team"],
    overview:
      "**Users** is where a Company admin manages the people in the company — invite new users, set or change their role, edit their name/email, disable and re-enable them, or remove them.\n\n" +
      "Roles are a ladder: **Viewer** (read-only) → **Operator** (can run rollouts) → **Admin** (full company management).",
    concepts: [
      { term: "Roles", definition: "**Viewer** reads everything operational. **Operator** can also create and control rollout tasks. **Admin** (Company admin) manages users, keys, firmware, settings and the audit log." },
      { term: "Status", definition: "**Active**, **Invited** (invitation sent, not yet accepted) or **Deactivated** (blocked from signing in, data kept)." },
      { term: "Disable vs delete", definition: "**Disable** blocks sign-in but keeps the account. **Delete** removes the user from the list and disables sign-in; the record is retained 30 days then permanently removed." },
    ],
    tasks: [
      {
        id: "invite-user",
        title: "Invite a user",
        minRole: "tenant_admin",
        steps: [
          { text: "Open **Users** and click **Invite User**." },
          { text: "Enter their **Email** (and optionally first/last name), and pick a **Role** (Viewer, Operator or Admin)." },
          { text: "Click **Send Invite**. They appear as **Invited** until they set their password and sign in." },
        ],
      },
      {
        id: "change-role",
        title: "Change someone's role",
        minRole: "tenant_admin",
        steps: [
          { text: "In the user's row, choose a new value in the **Role** dropdown — it applies immediately." },
          { text: "Or click **Edit** to change name, email and role together, then **Save**." },
        ],
      },
      {
        id: "disable-delete-user",
        title: "Disable, re-enable or remove a user",
        minRole: "tenant_admin",
        steps: [
          { text: "**Disable**: click **Disable** and confirm — they can't sign in until re-enabled." },
          { text: "**Enable**: for a deactivated user, click **Enable**." },
          { text: "**Delete**: click **Delete** and confirm — they're removed from the list (record kept 30 days)." },
        ],
      },
    ],
    faqs: [
      { q: "I can't edit a 'super admin' row.", a: "Platform-admin (super admin) accounts are managed by Riot IO and are read-only in your Users list — no role change, edit, disable or delete." },
      { q: "Why was I stopped from deleting or demoting someone?", a: "The platform protects against removing your last admin (and similar safety rules). If a delete/disable is blocked, the reason is shown above the table." },
    ],
    roleMatrix: [
      { capability: "See the user list", viewer: "no", operator: "no", tenant_admin: "yes", super_admin: "yes" },
      { capability: "Invite users & set roles", viewer: "no", operator: "no", tenant_admin: "yes", super_admin: "yes" },
      { capability: "Disable / delete users", viewer: "no", operator: "no", tenant_admin: "yes", super_admin: "yes" },
    ],
    related: ["company", "audit-log", "profile"],
  },

  // ─────────────────────────────────────────── Audit Log
  {
    id: "audit-log",
    title: "Audit Log",
    icon: ScrollText,
    group: "management",
    summary: "Read the immutable record of who did what and when across your company.",
    routeMatch: ["/audit-log"],
    minRole: "tenant_admin",
    keywords: ["audit", "log", "history", "who did what", "compliance", "security", "immutable", "record"],
    overview:
      "The **Audit Log** is a read-only, tamper-proof record of security-relevant events in your company — rollouts, firmware changes, user and settings changes, sign-ins and more — showing the action, the entity, the actor and the time.\n\n" +
      "Entries can't be edited or deleted; this is your compliance and troubleshooting trail. Company admins only.",
    concepts: [
      { term: "Action", definition: "What happened — Create, Update, Delete or Login — shown as a coloured badge." },
      { term: "Entity", definition: "What it happened to — a Task, Firmware, Device, Company, User, Group Key, and so on — with a (truncated) entity ID." },
      { term: "Actor", definition: "Who did it — a user's email, or 'system' for automated actions." },
    ],
    tasks: [
      {
        id: "search-audit",
        title: "Find a specific event",
        minRole: "tenant_admin",
        steps: [
          { text: "Open **Audit Log**." },
          { text: "Use the **Action** dropdown to filter by Create / Update / Delete / Login." },
          { text: "Use the search field (Entity ID, Entity type or User) to narrow the current page, and page through with the arrows." },
        ],
      },
    ],
    faqs: [
      { q: "Can I edit or remove an entry?", a: "No — the audit log is immutable by design. There are no row actions." },
      { q: "Can I export it?", a: "Yes — set the filters you want and choose **Export**. Every matching event in the last 30 days (not just the page on screen) is written to a CSV file, which downloads when it's ready. Keep the page open until it does." },
    ],
    related: ["users", "company", "api-credentials"],
  },

  // ─────────────────────────────────────────── Notifications
  {
    id: "notifications",
    title: "Notifications",
    icon: Bell,
    group: "management",
    summary: "Your inbox of system alerts, and the preferences that control which ones you get.",
    routeMatch: ["/notifications"],
    keywords: ["notifications", "alerts", "inbox", "unread", "mark read", "preferences", "email", "in-app"],
    overview:
      "**Notifications** is your personal inbox of system alerts — task completions and failures, devices going offline, firmware uploads, invitations, and tier-limit warnings. Unread items are highlighted.\n\n" +
      "Use **Preferences** to choose, per event, whether you're notified **in-app**, by **email**, both, or neither. Available to every signed-in user.",
    concepts: [
      { term: "Unread count", definition: "The gold pill shows how many notifications you haven't read. Mark items read individually with the **Mark read** button." },
      { term: "Channels", definition: "Each event type can notify you **In-App** and/or by **Email** — set independently in Preferences." },
    ],
    tasks: [
      {
        id: "read-notifications",
        title: "Read and clear notifications",
        steps: [
          { text: "Open **Notifications**." },
          { text: "Highlighted rows are unread; click **Mark read** on each to clear it." },
          { text: "Page through older alerts with **Previous / Next**." },
        ],
      },
      {
        id: "set-preferences",
        title: "Choose which notifications you receive",
        steps: [
          { text: "From Notifications, click **Preferences** (top-right)." },
          { text: "For each event, tick or untick **In-App** and **Email**." },
          { text: "Click **Save Preferences**." },
        ],
      },
    ],
    faqs: [
      { q: "Is there a 'mark all read'?", a: "Not currently — mark each unread item individually. Reading is one-way (you can't mark something unread again)." },
    ],
    related: ["profile", "usage"],
  },

  // ─────────────────────────────────────────── Profile
  {
    id: "profile",
    title: "Profile",
    icon: User,
    group: "management",
    summary: "See your role and company, and manage how you sign in.",
    routeMatch: ["/profile"],
    keywords: ["profile", "account", "role", "tenant", "password", "sign in", "me"],
    overview:
      "**Profile** shows your own account context — your role and the company (tenant) you belong to.\n\n" +
      "For sign-in security, use **Passkeys** to add phishing-resistant sign-in, and note that admin accounts are enrolled in two-factor authentication during sign-in.",
    concepts: [
      { term: "Role", definition: "Your permission level — Viewer, Operator, Company admin or Platform admin. Only a Company admin (or Riot IO) can change it." },
      { term: "Tenant", definition: "The company your account belongs to, shown by its Tenant ID." },
    ],
    tasks: [
      {
        id: "check-profile",
        title: "Check my role and company",
        steps: [
          { text: "Open **Profile** from the Management menu." },
          { text: "Read your **Role** and **Tenant** in the top card." },
          { text: "If your role is wrong, ask a Company admin to update it under **Users**." },
        ],
      },
    ],
    faqs: [
      { q: "How do I change my password?", a: "The password fields on this page aren't active in this release. To set or reset a password, use **Forgot password** on the sign-in screen; for stronger sign-in, add a **Passkey**." },
      { q: "How do I secure my sign-in?", a: "Add a **Passkey** (Touch ID / Face ID / security key). Admin accounts are additionally prompted to set up an authenticator app (two-factor) during sign-in." },
    ],
    related: ["passkeys", "notifications", "users"],
  },

  // ─────────────────────────────────────────── Passkeys
  {
    id: "passkeys",
    title: "Passkeys & 2FA",
    icon: KeyRound,
    group: "management",
    summary: "Add phishing-resistant passkeys, and understand the authenticator-app two-factor step.",
    routeMatch: ["/settings/passkeys"],
    keywords: ["passkey", "webauthn", "fido", "touch id", "face id", "security key", "mfa", "2fa", "totp", "authenticator", "two-factor"],
    overview:
      "**Passkeys** let you sign in with your device's biometrics (Touch ID, Face ID, Windows Hello) or a hardware security key instead of, or alongside, a password — phishing-resistant and quick. This page lists your enrolled passkeys and lets you add or remove them.\n\n" +
      "Available to every signed-in user, for their own account.",
    concepts: [
      { term: "Passkey", definition: "A cryptographic sign-in credential tied to your device or security key. Nothing secret is typed, so it can't be phished." },
      { term: "Two-factor (TOTP)", definition: "Admin accounts without a second factor are prompted to set up an authenticator app during sign-in: scan a QR code, then enter the 6-digit code. After that you're asked for a code each time you sign in." },
    ],
    tasks: [
      {
        id: "add-passkey",
        title: "Register a passkey",
        steps: [
          { text: "Open **Passkeys** and click **Register a new passkey**." },
          { text: "Follow your device's prompt (Touch ID / Face ID / Windows Hello / security key)." },
          { text: "The passkey appears in your list once enrolled." },
        ],
      },
      {
        id: "remove-passkey",
        title: "Remove a passkey",
        steps: [
          { text: "Find the passkey in the list and click **Remove**, then confirm." },
          { text: "Make sure you keep another way to sign in before removing your only passkey." },
        ],
      },
      {
        id: "setup-2fa",
        title: "Set up authenticator-app two-factor (admins)",
        intro: "Admin accounts are guided through this at sign-in when no second factor exists yet.",
        steps: [
          { text: "At sign-in, scan the shown **QR code** with an authenticator app (Google Authenticator, 1Password, Authy…), or enter the secret manually." },
          { text: "Type the app's **6-digit code** and confirm." },
          { text: "Sign in again — you'll be asked for a fresh code each time." },
        ],
      },
    ],
    faqs: [
      { q: "The Passkeys page shows a 'not available in local dev' banner.", a: "Passkeys need a real sign-in session, so the page is disabled in a local/demo environment. Use it against the real dev or production app." },
      { q: "Can I rename a passkey?", a: "No — the name is assigned automatically. You can remove and re-add if needed." },
    ],
    related: ["profile", "users"],
  },
];
